import {
  ControlListProps,
  ControlListConfirmationDialogProps,
  useTranslations,
  useFormData,
  useRuntimeEnv,
  useRuntimeEnvRolesIncludes,
  useUrlParamsValue,
  TTimeOff,
} from "@harea/component";
import { useMemo, useEffect, useRef } from "react";
import { AppointmentVacationBalance } from "../../vacation/components/AppointmentVacationBalance";
import { useTimeOffTypeList } from "../../../hooks/useTimeOffTypeList";
import { useContractManagerQuery } from "hooks/useContractManagerQuery";
import moment from "moment";
import { useDateIncludeWorkingDays } from "hooks/useDateIncludeWorkingDays";
import { useDateDifferenceExcludingWeekends } from "hooks/useDateDifferenceExcludingWeekends";
import { useDailyQuotaLeaveTypes } from "hooks/useDailyQuotaLeaveTypes";
import { useWorkingDayListQuery } from "../../../../calendar/hooks/useWorkingDayListQuery";
import { WorkingDayCheckControl } from "../components/WorkingDayCheckControl";
import { useWorkingDaysInPeriod } from "./useWorkingDaysInPeriod";
import { useTimeOffOverlapQuery } from "./useTimeOffOverlapQuery";
import { useEmployeeOrganisationQuery } from "./useEmployeeOrganisationQuery";
import { useEmployeeGenderQuery } from "./useEmployeeGenderQuery";

const CRITICAL_DAY_ENABLED_TYPES = ["PERMISSION", "ABSENCE", "SICK_LEAVE"];
const PAST_DATE_ENABLED_TYPES = ["ABSENCE", "SICK_LEAVE"];

export const useTimeOffControlForm = (
  id?: number,
  type?: string,
): ControlListProps => {
  const [
    DATE_BEGIN,
    DATE_END,
    HOUR_BEGIN,
    HOUR_END,
    SAVE,
    CANCEL,
    ATTACHMENT,
    REASON,
    LABELS,
    TYPE_LABEL,
    BALANCE,
    EMPLOYEE,
    END_DATE_ERROR,
    START_DATE_ERROR,
    INSUFICIENT_QUOTA,
    INCLUDE_WORKING_DAYS_ERROR,
    REMAINING_QUOTA,
    CONFIRMATION,
    HOUR_END_SAME_ERROR,
    IS_CRITICAL_DAY,
    OVERLAP_ERROR_MESSAGE,
    OVERLAP_TITLE,
    OVERLAP_CLOSE,
    PATERNAL_MAX_DAYS_ERROR,
    PERMISSION_OVERFLOW_TITLE,
    PERMISSION_OVERFLOW_LABEL,
    PERMISSION_OVERFLOW_LEAVE,
    PERMISSION_OVERFLOW_EXCEPTION,
    PERMISSION_OVERFLOW_CONFIRM,
    INSUFFICIENT_BALANCE_TITLE,
    INSUFFICIENT_BALANCE_LABEL,
    INSUFFICIENT_BALANCE_OPTION_NEGATIVE,
    INSUFFICIENT_BALANCE_OPTION_PAYROLL,
    INSUFFICIENT_BALANCE_OPTION_EXCEPTION,
    INSUFFICIENT_BALANCE_CONFIRM,
    DELIVERY_DATE,
    PREGNANCY_CERTIFICATE,
    LEAVE_REQUEST,
    MATERNITY_BOOKLET,
    MEDICAL_PROOFS,
    START_DATE_PAST_ERROR,
  ] = useTranslations(i18n);

  const urlParams = useUrlParamsValue();
  const { formData, setFormData } = useFormData<Partial<TTimeOff>>({
    id: TIMEOFF_FORM_ADD_ID,
    formData: {},
  });
  const includeWorkingDays = useDateIncludeWorkingDays(formData?.employee?.id);
  const getDateDifferenceExcludingWeekends =
    useDateDifferenceExcludingWeekends();
  const typeAll = urlParams?.type === "all";

  const isEmployeePreselected =
    !!urlParams?.employeeFilterCalendar &&
    urlParams?.employeeFilterCalendar !== -1;
  const employeeId = formData?.employee?.id;
  const employeeGenderFromSelection =
    (formData?.employee as any)?.gender ??
    (formData?.employee as any)?.user?.gender ??
    (formData?.employee as any)?.employee?.gender ??
    (formData?.employee as any)?.user?.employee?.gender;
  const employeeGenderQuery = useEmployeeGenderQuery(employeeId);
  const employeeGender = employeeGenderFromSelection ?? employeeGenderQuery;
  const timeOffTypes = useTimeOffTypeList(
    !typeAll ? "LEAVE" : undefined,
    employeeId ? employeeGender : undefined,
  );

  const getDesignation = (value?: unknown) =>
    timeOffTypes?.find((t) => t.value === value)?.value as string | undefined;

  const resolveDesignation = (value: any): string | undefined => {
    const typeValue = value?.type as any;
    const typeObj: any =
      typeValue && typeof typeValue === "object"
        ? typeValue
        : timeOffTypes?.find((t) => t.value === typeValue);
    return (typeObj?.designation ?? typeObj?.value) as string | undefined;
  };

  const isMaternity = useMemo(() => {
    const designation = getDesignation(formData?.type);
    return designation === "MATERNAL" || designation === "MATERNAL_ASSISTANCE";
  }, [formData?.type, timeOffTypes]);

  const shouldHideJustificationFiles = useMemo(() => {
    const designation = resolveDesignation({ type: formData?.type });
    return designation === "MATERNAL";
  }, [formData?.type, timeOffTypes]);

  useEffect(() => {
    const isTypeSelectRendered = !window.location.pathname.includes("edit");
    if (
      isTypeSelectRendered &&
      typeof formData?.type === "string" &&
      formData.type &&
      timeOffTypes?.length &&
      !timeOffTypes.some((option) => option.value === formData.type)
    ) {
      setFormData((value) => ({ ...value, type: undefined }));
    }
  }, [formData?.type, employeeId, timeOffTypes, setFormData]);
  const { runtimeEnv } = useRuntimeEnv(true);
  const roleIncludes = useRuntimeEnvRolesIncludes(true);
  const salarieEmployeeId = roleIncludes("DASHBOARD_SALARIE")
    ? (runtimeEnv?.personnel as any)?.id
    : undefined;

  useEffect(() => {
    if (
      !id &&
      !urlParams?.bulk &&
      salarieEmployeeId &&
      formData?.employee?.id !== salarieEmployeeId
    ) {
      setFormData((value) => ({ ...value, employee: { id: salarieEmployeeId } as any }));
    }
  }, [id, urlParams?.bulk, salarieEmployeeId, formData?.employee?.id, setFormData]);
  const workingHourBegin =
    runtimeEnv?.organisation?.profile?.workHourBegin ?? "06:00";
  const workingHourEnd =
    runtimeEnv?.organisation?.profile?.workHourEnd ?? "17:00";
  const workRestHourEnd =
    runtimeEnv?.organisation?.profile?.workRestHourEnd ?? "13:00";

  const { data: contractData } = useContractManagerQuery(
    formData?.employee?.id,
    formData?.type as any,
  );
  const dailyQuotaTypes = useDailyQuotaLeaveTypes();
  const currentYear = moment(formData?.dateBegin).year() || moment().year();
  const { data: workingDaysData } = useWorkingDayListQuery(
    false,
    currentYear,
    formData?.employee?.id,
  );
  const getWorkingDaysInPeriod = useWorkingDaysInPeriod();

  const { data: overlapData } = useTimeOffOverlapQuery(formData, {
    enabled: true,
    transformErrorMessage: () => OVERLAP_ERROR_MESSAGE,
  });
  const hasOverlap = (overlapData?.data?.length ?? 0) > 0;

  const employeeOrganisation = useEmployeeOrganisationQuery(formData?.employee?.id);
  const employeeProfile =
    employeeOrganisation?.data?.organisation?.profile;
  const unpaidLeaveMode =
    employeeProfile?.unpaidLeaveMode ??
    runtimeEnv?.organisation?.profile?.unpaidLeaveMode ??
    "PAYROLL";
  const allowExceptionalAbsence =
    employeeProfile?.allowExceptionalAbsence ??
    runtimeEnv?.organisation?.profile?.allowExceptionalAbsence;

  const getRemainingQuota = () =>
    (contractData?.leaveBalance as any)?.getRemainingQuota ?? 0;

  const getRequestedAmount = (value: { dateBegin?: any; dateEnd?: any; hourBegin?: any; hourEnd?: any }) =>
    getDateDifferenceExcludingWeekends(
      moment(value?.dateBegin),
      moment(value?.dateEnd),
      value?.hourBegin,
      value?.hourEnd,
    );

  const isDailyQuotaType = (designation?: string) =>
    !!designation && dailyQuotaTypes.includes(designation);

  const isPermissionQuotaExceeded = useMemo(() => {
    if (
      !dailyQuotaTypes.includes("PERMISSION") ||
      !formData?.dateBegin ||
      !formData?.dateEnd
    ) {
      return false;
    }
    if (getDesignation(formData?.type) !== "PERMISSION") {
      return false;
    }
    return getRequestedAmount(formData) > getRemainingQuota();
  }, [
    formData?.type,
    formData?.dateBegin,
    formData?.dateEnd,
    formData?.hourBegin,
    formData?.hourEnd,
    timeOffTypes,
    contractData,
    dailyQuotaTypes,
    getDateDifferenceExcludingWeekends,
  ]);

  const soldeLabel = useMemo(() => {
    if (!formData?.type || !contractData?.leaveBalance) {
      return BALANCE;
    }
    return isDailyQuotaType(getDesignation(formData?.type))
      ? REMAINING_QUOTA
      : BALANCE;
  }, [
    formData?.type,
    contractData?.leaveBalance,
    timeOffTypes,
    dailyQuotaTypes,
    BALANCE,
    REMAINING_QUOTA,
  ]);

  const form = useMemo(
    () => [
      {
        type: "hidden",
        name: "id",
      },
      [
        {
          type: "select",
          name: "employee",
          label: EMPLOYEE,
          required: true,
          valueObject: true,
          valueProperty: "id",
          ...(salarieEmployeeId
            ? { readonly: true }
            : isEmployeePreselected
              ? {
                  readonly: true,
                  value: { id: urlParams.employeeFilterCalendar },
                }
              : {}),
          GQLProps: {
            gql: `
                    query {
                        contracts (filter:{data:{user:{department:{id:${(runtimeEnv?.user as any)?.department?.id ?? null}}}},sort: [{ sort: "ASC", value: "user.fullName" }] }) {
                            data{
                                user {
                                    id fullName 
                                    profilePicture { id }
                                    department { id designation }
                                    gender
                                }
                            }
                        }
                    }
            `,
            rootProp: "contracts.data",
            labelProp: "user.fullName",
            valueProp: "user.id",
            userIdProp: "user.id",
          },
        },
        {
          type: "solde",
          name: "solde",
          label: soldeLabel,
        },
      ],
      ...(!window.location.pathname.includes("edit")
        ? [
            {
              type: "select",
              name: "type",
              label: TYPE_LABEL,
              required: true,
              options: timeOffTypes,
            },
          ]
        : []),
      ...(isMaternity
        ? [
            {
              type: "date-picker",
              name: "probableDeliveryDate",
              label: DELIVERY_DATE,
              required: true,
              autoComplete: "off",
            },
            {
              type: "file",
              name: "pregnancyCertificate",
              label: PREGNANCY_CERTIFICATE,
              required: true,
            },
            {
              type: "file",
              name: "leaveRequest",
              label: LEAVE_REQUEST,
            },
            {
              type: "file",
              name: "maternityBooklet",
              label: MATERNITY_BOOKLET,
            },
            {
              type: "file",
              name: "medicalProofs",
              label: MEDICAL_PROOFS,
            },
          ]
        : []),
      ...(!isMaternity
        ? [
            [
              {
                type: "date-picker",
                name: "dateBegin",
                label: DATE_BEGIN,
                required: true,
                autoComplete: "off",
                minDate:
                  !id && !PAST_DATE_ENABLED_TYPES.includes(formData?.type as any)
                    ? moment().toDate()
                    : undefined,
              },
              {
                type: "hour",
                name: "hourBegin",
                interval: 60,
                minHour: workingHourBegin,
                maxHour: workingHourEnd,
                value: workingHourBegin,
                label: HOUR_BEGIN,
              },
            ],
            [
              {
                type: "date-picker",
                name: "dateEnd",
                label: DATE_END,
                required: true,
                autoComplete: "off",
              },
              {
                type: "hour",
                name: "hourEnd",
                options: [
                  { label: workingHourBegin, value: workingHourBegin },
                  { label: workRestHourEnd, value: workRestHourEnd },
                ],
                value: workingHourBegin,
                label: HOUR_END,
              },
            ],
          ]
        : []),
      {
        type: "textarea",
        name: "reason",
        label: REASON,
        required: true,
      },
      [
        {
          type: "lov-select",
          multiple: true,
          name: "labels",
          label: LABELS,
          tp: "APPOINTMENT_LABELS",
        },
        ...(!shouldHideJustificationFiles
          ? [
              {
                type: "file",
                name: "files",
                label: ATTACHMENT,
                multiple: true,
                required: !["LEAVE", "ABSENCE"]?.includes(type!),
              },
            ]
          : []),
      ],
    ],
    [
      soldeLabel,
      timeOffTypes,
      EMPLOYEE,
      TYPE_LABEL,
      DATE_BEGIN,
      HOUR_BEGIN,
      DATE_END,
      HOUR_END,
      REASON,
      LABELS,
      ATTACHMENT,
      type,
      workingHourBegin,
      workingHourEnd,
      workRestHourEnd,
      runtimeEnv,
      isEmployeePreselected,
      salarieEmployeeId,
      urlParams?.employeeFilterCalendar,
      isMaternity,
      shouldHideJustificationFiles,
      DELIVERY_DATE,
      PREGNANCY_CERTIFICATE,
      LEAVE_REQUEST,
      MATERNITY_BOOKLET,
      MEDICAL_PROOFS,
      id,
      formData?.type,
    ],
  );

  const MATERNITY_PRENATAL_DAYS = 42;
  const MATERNITY_TOTAL_DAYS = 98;
  const MATERNITY_POSTNATAL_DAYS = MATERNITY_TOTAL_DAYS - MATERNITY_PRENATAL_DAYS - 1;

  const wasMaternity = useRef(isMaternity);

  useEffect(() => {
    const previouslyMaternity = wasMaternity.current;
    wasMaternity.current = isMaternity;

    if (previouslyMaternity && !isMaternity) {
      setFormData((prev) => ({
        ...prev,
        probableDeliveryDate: undefined,
        pregnancyCertificate: undefined,
        leaveRequest: undefined,
        maternityBooklet: undefined,
        medicalProofs: undefined,
        dateBegin: undefined,
        dateEnd: undefined,
      }));
    }
  }, [isMaternity, setFormData]);

  useEffect(() => {
    if (isMaternity && formData?.probableDeliveryDate && moment(formData.probableDeliveryDate, true).isValid()) {
      const delivery = moment(formData.probableDeliveryDate);
      const dateBegin = delivery.clone().subtract(MATERNITY_PRENATAL_DAYS, 'days').format('YYYY-MM-DD');
      const dateEnd = delivery.clone().add(MATERNITY_POSTNATAL_DAYS, 'days').format('YYYY-MM-DD');
      
      if (formData.dateBegin !== dateBegin || formData.dateEnd !== dateEnd) {
        setFormData(prev => ({
          ...prev,
          dateBegin,
          dateEnd
        }));
      }
    } else if (isMaternity && !formData?.probableDeliveryDate) {
      setFormData(prev => ({
        ...prev,
        dateBegin: '',
        dateEnd: ''
      }));
    }
  }, [isMaternity, formData?.probableDeliveryDate, setFormData]);

  const validateDateField = (value: any, formData_: any) => {
    const { isWorkingDay, isCriticalDay } = includeWorkingDays(moment(value));
    if (isCriticalDay && !CRITICAL_DAY_ENABLED_TYPES.includes(formData_?.type)) {
      return IS_CRITICAL_DAY;
    }
    if (!isWorkingDay) {
      return INCLUDE_WORKING_DAYS_ERROR;
    }
  };

  const handleValidate = (name: string, value: any, formData_: any) => {
    if (name === "dateBegin") {
      if (
        !id &&
        !isMaternity &&
        !PAST_DATE_ENABLED_TYPES.includes(formData_?.type) &&
        moment(value).isBefore(moment(), "day")
      ) {
        return START_DATE_PAST_ERROR;
      }
      const dateError = validateDateField(value, formData_);
      if (dateError) {
        return dateError;
      }
      const dateBeginIsAfterDateEnd = moment(
        formData_?.dateBegin,
        "YYYY-MM-DD",
      )?.isAfter(moment(formData_?.dateEnd, "YYYY-MM-DD"));
      if (formData_?.dateBegin && dateBeginIsAfterDateEnd) {
        return START_DATE_ERROR;
      }
    }
    if (name === "dateEnd") {
      const dateError = validateDateField(value, formData_);
      if (dateError) {
        return dateError;
      }

      const endDateIsBefore = moment(formData_?.dateEnd, "YYYY-MM-DD").isBefore(
        moment(formData_?.dateBegin, "YYYY-MM-DD"),
      );
      if (formData_?.dateBegin && endDateIsBefore) {
        return END_DATE_ERROR;
      }

      const designation = getDesignation(formData_?.type);

      if (isDailyQuotaType(designation)) {
        const amount = getRequestedAmount(formData_);

        if (designation === "PATERNAL" && amount > 3) {
          return PATERNAL_MAX_DAYS_ERROR;
        }

        if (amount > getRemainingQuota() && designation !== "PERMISSION") {
          return INSUFICIENT_QUOTA;
        }
      }
    }

    if (name === "hourEnd") {
      const datesIsSame = moment(formData_?.dateEnd, "YYYY-MM-DD").isSame(
        moment(formData_?.dateBegin, "YYYY-MM-DD"),
      );

      if (datesIsSame && formData_?.hourBegin === formData_?.hourEnd) {
        return HOUR_END_SAME_ERROR;
      }
    }
  };

  return {
    data: {
      forms: form,
      fields: {
        solde: AppointmentVacationBalance,
      },
      value: {},
      next: {
        label: SAVE,
        className: "btn-primary ",
      },
      back: {
        label: CANCEL,
      },
      graphQL: {
        entity: "timeoff",
        entityId: id,
        enableFetch: !!id,
        mutationConfig: {
          transformErrorMessage: (e) => {
            if (e.response?.errors?.[0]?.message?.includes("OVERLAP_ERROR")) {
              return OVERLAP_ERROR_MESSAGE;
            }
            return undefined;
          },
        },
      },
    },
    validate: handleValidate,
    onBeforeSubmitDialog: (value) => {
      const dialogs: ControlListConfirmationDialogProps[] = [];

      if (hasOverlap) {
        dialogs.push({
          title: OVERLAP_TITLE,
          message: OVERLAP_ERROR_MESSAGE,
          confirmLabel: OVERLAP_CLOSE,
          onClose: () => {},
          onConfirm: () => {},
          onCancel: () => {},
        });
      }

      if (isPermissionQuotaExceeded) {
        dialogs.push({
          onClose: () => {},
          onConfirm: () => {},
          onCancel: () => {},
          title: PERMISSION_OVERFLOW_TITLE,
          confirmationForm: {
            title: PERMISSION_OVERFLOW_LABEL,
            forms: [
              {
                type: "select",
                name: "permissionOverflowPreference",
                required: true,
                options: [
                  { label: PERMISSION_OVERFLOW_LEAVE, value: "LEAVE" },
                  { label: PERMISSION_OVERFLOW_EXCEPTION, value: "EXCEPTION" },
                ],
              },
            ],
            next: {
              label: PERMISSION_OVERFLOW_CONFIRM,
            },
          },
        });
      }

      const designation = getDesignation(value?.type);

      if (
        !isNaN(Number(value?.solde)) &&
        !isDailyQuotaType(designation) &&
        designation !== "ABSENCE" &&
        !window.location.pathname.includes("missings")
      ) {
        if (getRequestedAmount(value) > Number(value?.solde)) {
          dialogs.push({
            onClose: () => {},
            onConfirm: () => {},
            onCancel: () => {},
            title: INSUFFICIENT_BALANCE_TITLE,
            confirmationForm: {
              title: INSUFFICIENT_BALANCE_LABEL,
              forms: [
                {
                  type: "select",
                  name: "unpaidLeave",
                  required: true,
                  options: [
                    {
                      label: INSUFFICIENT_BALANCE_OPTION_NEGATIVE,
                      value: "negativeBalance",
                    },
                    {
                      label: INSUFFICIENT_BALANCE_OPTION_PAYROLL,
                      value: "deductFromPay",
                    },
                    {
                      label: INSUFFICIENT_BALANCE_OPTION_EXCEPTION,
                      value: "exception",
                    },
                  ],
                },
              ],
              next: {
                label: INSUFFICIENT_BALANCE_CONFIRM,
              },
            },
          });
        }
      }

      if (workingDaysData?.data && formData?.dateBegin && formData?.dateEnd) {
        const workingDaysInPeriod = getWorkingDaysInPeriod(
          workingDaysData?.data,
          formData.dateBegin,
          formData.dateEnd,
        );

        if (workingDaysInPeriod.length > 0) {
          dialogs.push({
            onClose: () => {},
            onConfirm: () => {},
            onCancel: () => {},
            title: CONFIRMATION,
            confirmationForm: {
              forms: [
                {
                  type: "working_days_check",
                  name: "accountableWorkingDays",
                  workingDays: workingDaysInPeriod,
                },
              ],
              fields: { working_days_check: WorkingDayCheckControl },
            },
          });
        }
      }

      return dialogs.length > 0 ? dialogs : null;
    },
    onBeforeSaving: (value) => {
      if (hasOverlap) {
        return false;
      }

      const typeValue = value?.type as any;
      const typeObj: any =
        typeValue && typeof typeValue === "object"
          ? typeValue
          : timeOffTypes?.find((t) => t.value === typeValue);
      const designation = (typeObj?.designation ?? typeObj?.value) as
        | string
        | undefined;
      const value_ = { ...value };

      if (!value?.id) {
        value_.type = { id: typeObj?.id };
      }

      const unpaidLeaveChoice = value?.confirmationData?.unpaidLeave;
      let mode =
        value?.unpaidLeaveMode ??
        (!typeObj?.isPaid ? unpaidLeaveMode : undefined);
      let negativeBalance = false;
      let deductFromPay = false;
      let exception = false;

      if (unpaidLeaveChoice) {
        switch (unpaidLeaveChoice) {
          case "negativeBalance":
            negativeBalance = true;
            mode = "BALANCE";
            break;
          case "deductFromPay":
            deductFromPay = true;
            mode = "PAYROLL";
            break;
          case "exception":
            exception = true;
            mode = "EXCEPTION";
            break;
        }
      }

      if (designation === "ABSENCE" && mode === "BALANCE") {
        mode = "PAYROLL";
      }
      if (mode) {
        value_.unpaidLeaveMode = mode;
        value_.isExceptionalPaid = exception;
        if (mode === "EXCEPTION" && allowExceptionalAbsence) {
          value_.isExceptionalPaid = true;
        }
      }

      value_.negativeBalance = negativeBalance;
      value_.deductFromPay = deductFromPay;
      value_.exception = exception;

      const permissionPreference =
        value?.confirmationData?.permissionOverflowPreference;
      if (isPermissionQuotaExceeded && permissionPreference) {
        value_.permissionOverflowPreference = permissionPreference;
      }

      value_.hourBegin = value?.hourBegin ? value?.hourBegin : workingHourBegin;
      value_.hourEnd = value?.hourEnd ? value?.hourEnd : workingHourBegin;

      if (value?.solde && designation !== "ABSENCE") {
        const start = moment(value?.dateBegin);
        const end = moment(value?.dateEnd);
        const totalDays = end.diff(start, "days") + 1;
        value_.solde = totalDays;
      } else if (designation === "ABSENCE") {
        delete value_.solde;
      }

      if (value?.confirmationData?.accountableWorkingDays) {
        value_.accountableWorkingDays = JSON.stringify(
          value?.confirmationData?.accountableWorkingDays,
        );
      }

      if (!id) {
        value_.status = "new";
      }

      const shouldHideJustificationFilesOnSave =
        designation === "MATERNAL";
      if (shouldHideJustificationFilesOnSave) {
        delete value_.files;
      }

      if (isMaternity) {
        value_.probableDeliveryDate = value.probableDeliveryDate;
        value_.pregnancyCertificate = value.pregnancyCertificate;
        value_.leaveRequest = value.leaveRequest;
        value_.maternityBooklet = value.maternityBooklet;
        value_.medicalProofs = value.medicalProofs;

        value_.data = {
            probableDeliveryDate: value.probableDeliveryDate,
            pregnancyCertificate: value.pregnancyCertificate,
            leaveRequest: value.leaveRequest,
            maternityBooklet: value.maternityBooklet,
            medicalProofs: value.medicalProofs,
        };
      }

      return value_;
    },
    id: TIMEOFF_FORM_ADD_ID,
  };
};

const TIMEOFF_FORM_ADD_ID = "timeoff-add-leaving-form";

const i18n = [
  "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentBegin",
  "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentEnd",
  "BSMRH.Calendar.TimeOff.Create.Form.Label.HourAppointmentBegin",
  "BSMRH.Calendar.TimeOff.Create.Form.Label.HourAppointmentEnd",
  "std_save",
  "std_cancel",
  "std_justification",
  "std_reason",
  "std_labels",
  "std_type",
  "std_remaining_balance",
  "std_employee",
  "BSMRH.Calendar.TimeOff.Create.Form.EndDate.Error.Message",
  "BSMRH.Calendar.TimeOff.Create.Form.StartDate.Error.Message",
  "BSMRH.Calendar.TimeOff.Create.Form.InsufficientQuota.Error.Message",
  "BSMRH.Calendar.TimeOff.Create.Form.IncludeWorkingDays.Error.Message",
  "std_remaining_quota",
  "std_confirmation",
  "BSMRH.Calendar.TimeOff.Create.Form.HourEnd.Same.Error",
  "BSMRH.Calendar.TimeOff.Create.Form.IsCriticalDay.Error.Message",
  "BSMRH.Calendar.TimeOff.Create.Form.Overlap.Error.Message",
  "BSMRH.Calendar.TimeOff.Create.Overlap.Dialog.Title",
  "BSMRH.Calendar.TimeOff.Create.Overlap.Dialog.Close",
  "BSMRH.Calendar.TimeOff.Create.Form.PaternalMaxDays.Error.Message",
  "BSMRH.Calendar.TimeOff.Create.PermissionOverflow.Dialog.Title",
  "BSMRH.Calendar.TimeOff.Create.PermissionOverflow.Dialog.Label",
  "BSMRH.Calendar.TimeOff.Create.PermissionOverflow.Option.Leave",
  "BSMRH.Calendar.TimeOff.Create.PermissionOverflow.Option.Exception",
  "BSMRH.Calendar.TimeOff.Create.PermissionOverflow.Confirm",
  "BSMRH.Calendar.TimeOff.Create.InsufficientBalance.Dialog.Title",
  "BSMRH.Calendar.TimeOff.Create.InsufficientBalance.Dialog.Label",
  "BSMRH.Calendar.TimeOff.Create.InsufficientBalance.Option.NegativeBalance",
  "BSMRH.Calendar.TimeOff.Create.InsufficientBalance.Option.DeductFromPay",
  "BSMRH.Calendar.TimeOff.Create.InsufficientBalance.Option.ExceptionalApproval",
  "BSMRH.Calendar.TimeOff.Create.InsufficientBalance.Confirm",
  "std_probable_delivery_date",
  "std_pregnancy_certificate",
  "std_leave_request",
  "std_maternity_booklet",
  "std_medical_proofs",
  "BSMRH.Calendar.TimeOff.Create.Form.StartDate.Past.Error.Message",
];